import { ComponentType } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  ComponentRef,
  ElementRef,
  EnvironmentInjector,
  Inject,
  Injectable,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { Observable, Subscription, filter } from 'rxjs';
import { Global } from '../types/global.type';
import { LoadingScreenService } from './loading-screen.service';
import { LoggingService } from './logging.service';
import { NavigationService } from './navigation.service';
import { StorageService } from './storage.service';

export type SidePanelSize = 'small' | 'medium' | 'large' | 'full' | 'auto';
export type SidePanelMode = 'over' | 'push' | 'side';

export interface SidePanelOptions {
  size?: SidePanelSize;
  autosize?: boolean;
  hasBackdrop?: boolean;
  hasCloseButton?: boolean;
  cancelOnRouteChange?: boolean;
  showLoadingScreen?: boolean;
  panelClass?: string;
  mode?: SidePanelMode;
  resizable?: boolean;
}

interface PanelElements {
  sidepanel: HTMLElement | null;
  content: HTMLElement | null;
}

interface InitializableComponent {
  initialize: () => Promise<void>;
}

interface CloseableComponent {
  onClosing: () => Promise<boolean | void>;
}

@Injectable({
  providedIn: 'root',
})
export class SidepanelService implements OnDestroy {
  private readonly OPEN_TIMEOUT_MS = 10000;
  private readonly WINDOW_MARGIN_PX = 64;

  private readonly DEFAULT_OPTIONS: Readonly<Required<SidePanelOptions>> = {
    size: 'auto',
    autosize: true,
    hasBackdrop: false,
    hasCloseButton: false,
    cancelOnRouteChange: false,
    showLoadingScreen: true,
    mode: 'over',
    resizable: false,
    panelClass: '',
  } as const;

  private readonly SIZE_WIDTH_MAP: Record<SidePanelSize, number | (() => number)> = {
    small: 400,
    medium: 600,
    large: 800,
    full: () => Math.max(window.innerWidth - this.WINDOW_MARGIN_PX, Global.minSidenavWidth),
    auto: Global.minSidenavWidth,
  } as const;

  private readonly SELECTORS = {
    SIDEPANEL: '.side-panel',
    CONTENT: 'mat-sidenav-content',
  } as const;

  private panel!: MatSidenav;
  private panelElementRef: ElementRef | null = null;
  private vcf!: ViewContainerRef;
  private componentRef: ComponentRef<any> | null = null;
  private currentSettings: Required<SidePanelOptions> = { ...this.DEFAULT_OPTIONS };
  private routeChangeSubscription: Subscription | null = null;
  private resizeListener: (() => void) | null = null;

  get isOpened(): boolean {
    return this.panel?.opened ?? false;
  }

  get closed$(): Observable<boolean> {
    return this.panel?.openedChange.pipe(filter(opened => !opened)) ?? new Observable();
  }

  get settings(): SidePanelOptions {
    return { ...this.currentSettings };
  }

  get panelWidth(): number {
    if (!this.panel?.opened || !this.panelElementRef) {
      return Global.minSidenavWidth;
    }

    const element = this.panelElementRef.nativeElement as HTMLElement;
    return element.offsetWidth || Global.minSidenavWidth;
  }

  constructor(
    private readonly router: Router,
    private readonly navigationService: NavigationService,
    private readonly loggingService: LoggingService,
    private readonly loadingScreenService: LoadingScreenService,
    private readonly storageService: StorageService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngOnDestroy(): void {
    this.cleanup();
  }

  setPanel(sidenav: MatSidenav, elementRef?: ElementRef): void {
    this.panel = sidenav;
    this.panelElementRef = elementRef || null;
  }

  /**
   * Sets the view container reference for dynamic component creation
   */
  setContentVcf(viewContainerRef: ViewContainerRef): void {
    this.vcf = viewContainerRef;
  }

  /**
   * Opens a template in the sidepanel
   */
  async openTemplate(template: TemplateRef<any>): Promise<void> {
    this.vcf.clear();
    this.vcf.createEmbeddedView(template);
    await this.panel.open();
  }

  /**
   * Opens a component in the sidepanel with the specified options
   */
  async open<T>(
    component: ComponentType<T>,
    options: SidePanelOptions,
    setParameters?: (instance: T) => void,
    environmentInjector?: EnvironmentInjector,
  ): Promise<void> {
    try {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error(`Sidepanel open timeout after ${this.OPEN_TIMEOUT_MS / 1000} seconds`)),
          this.OPEN_TIMEOUT_MS,
        ),
      );

      await Promise.race([this.openInternal(component, options, setParameters, environmentInjector), timeoutPromise]);
    } catch (error) {
      this.handleError(error, 'open');
      throw new Error(`Sidepanel open failed: ${this.getErrorMessage(error)}`);
    }
  }

  async close(): Promise<void> {
    const canClose = await this.checkCanClose();
    if (!canClose) {
      return;
    }

    this.cleanup();

    if (this.panel?.opened) {
      await this.panel.close();
    }

    this.resetAllStyles();
    this.setScrollableBody(true);
  }

  async toggle(): Promise<void> {
    await this.panel.toggle();
  }

  scrollToTop(): void {
    const scrollableElement = this.panelElementRef?.nativeElement?.querySelector('.mat-sidenav-content');
    if (scrollableElement) {
      scrollableElement.scrollTop = 0;
    }
  }

  updateMode(mode: SidePanelMode): void {
    if (this.panel && this.currentSettings) {
      this.currentSettings.mode = mode;
      this.panel.mode = mode;
    }
  }

  getCurrentMode(): SidePanelMode {
    return this.currentSettings.mode;
  }

  private async openInternal<T>(
    component: ComponentType<T>,
    options: SidePanelOptions,
    setParameters?: (instance: T) => void,
    environmentInjector?: EnvironmentInjector,
  ): Promise<void> {
    // Only update loading-related setting first to avoid template binding conflicts
    const showLoadingScreen = options.showLoadingScreen ?? this.DEFAULT_OPTIONS.showLoadingScreen;
    if (showLoadingScreen) {
      this.loadingScreenService.showLoadingScreen();
    }
    this.setScrollableBody(false);

    try {
      await this.preparePanel();

      if (this.shouldSkipComponentCreation(component)) {
        this.setScrollableBody(true);
        return;
      }

      await this.createAndSetupComponent(component, setParameters, environmentInjector);
      this.setupRouteChangeHandling();

      // Update settings and apply width BEFORE opening to ensure proper sizing
      this.updateSettings(options);
      this.applyPanelWidthBeforeOpen();

      await this.panel.open();
    } catch (error) {
      this.loggingService.logException(new Error(`Failed to open sidepanel component: ${component.name}`, { cause: error }));
      this.handleOpenError();
      this.cleanup(); // Ensure cleanup happens on error
      throw error;
    } finally {
      this.loadingScreenService.hideLoadingScreen();
    }
  }

  private async checkCanClose(): Promise<boolean> {
    if (!this.componentRef?.instance) {
      return true;
    }

    if (this.isCloseableComponent(this.componentRef.instance)) {
      const canClose = await this.componentRef.instance.onClosing();
      return canClose !== false;
    }

    return true;
  }

  private isCloseableComponent(instance: unknown): instance is CloseableComponent {
    return (
      instance !== null &&
      typeof instance === 'object' &&
      'onClosing' in instance &&
      typeof instance.onClosing === 'function'
    );
  }

  private cleanup(): void {
    this.destroyComponent();
    this.unsubscribeFromRouteChanges();
    this.removeResizeListener();
  }

  private destroyComponent(): void {
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }

  private unsubscribeFromRouteChanges(): void {
    if (this.routeChangeSubscription) {
      this.routeChangeSubscription.unsubscribe();
      this.routeChangeSubscription = null;
    }
  }

  private removeResizeListener(): void {
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }
  }

  private setupResizeListener(): void {
    this.removeResizeListener();
    if (this.currentSettings.size !== 'full' && !this.currentSettings.resizable) {
      return;
    }

    this.resizeListener = () => {
      if (!this.panel?.opened) {
        return;
      }

      const elements = this.getPanelElements();
      if (!elements.sidepanel) {
        return;
      }

      const targetWidth = this.calculateTargetWidth();
      this.applySidePanelWidth(elements.sidepanel, elements.content, targetWidth);
    };

    window.addEventListener('resize', this.resizeListener);
  }

  private updateSettings(options: SidePanelOptions): void {
    this.currentSettings = { ...this.DEFAULT_OPTIONS, ...options };
  }

  private async preparePanel(): Promise<void> {
    if (this.panel.opened) {
      await this.panel.close();
    }

    this.vcf.clear();
    this.destroyComponent();
  }

  private shouldSkipComponentCreation<T>(component: ComponentType<T>): boolean {
    const previousComponentName = this.componentRef?.instance?.constructor.name;
    const currentComponentName = component.prototype.constructor.name;
    return previousComponentName === currentComponentName;
  }

  private async createAndSetupComponent<T>(
    component: ComponentType<T>,
    setParameters?: (instance: T) => void,
    environmentInjector?: EnvironmentInjector,
  ): Promise<void> {
    if (!this.vcf) {
      throw new Error('ViewContainerRef is not set. Call setContentVcf() before opening sidepanel components.');
    }

    try {
      this.componentRef = this.vcf.createComponent(component, { environmentInjector });

      if (setParameters && this.componentRef.instance) {
        setParameters(this.componentRef.instance);
      }

      this.vcf.insert(this.componentRef.hostView);

      if (this.hasInitializeMethod(this.componentRef.instance)) {
        await this.componentRef.instance.initialize();
      }
    } catch (error) {
      this.loggingService.logException(new Error(`Failed to create or setup component: ${component.name}`, { cause: error }));
      throw error;
    }
  }

  private hasInitializeMethod(instance: unknown): instance is InitializableComponent {
    return (
      instance !== null &&
      typeof instance === 'object' &&
      'initialize' in instance &&
      typeof instance.initialize === 'function'
    );
  }

  private setupRouteChangeHandling(): void {
    if (!this.currentSettings.cancelOnRouteChange) {
      return;
    }

    this.routeChangeSubscription = this.navigationService.previousUrl$.subscribe(
      async (previousUrl: string | null) => {
        if (this.panel?.opened && this.hasRouteChanged(previousUrl)) {
          await this.close();
        }
      },
    );
  }

  private hasRouteChanged(previousUrl: string | null): boolean {
    const currentUrl = this.router.url.split('#')[0];
    const prevUrl = previousUrl?.split('#')[0];
    return currentUrl !== prevUrl;
  }

  private handleOpenError(): void {
    this.setScrollableBody(true);
  }

  private handleError(error: unknown, operation: string): void {
    this.loggingService.logException(error as Error);

    try {
      this.handleOpenError();
    } catch (handleError) {
      this.loggingService.logException(handleError as Error);
    }

    console.error(`Sidepanel failed to ${operation}:`, error);
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    return String(error) || 'Unknown error';
  }

  /**
   * Applies appropriate width to the sidepanel before opening
   */
  private applyPanelWidthBeforeOpen(): void {
    const elements = this.getPanelElements();
    if (!elements.sidepanel) {
      return;
    }

    const targetWidth = this.calculateTargetWidth();
    this.applySidePanelWidth(elements.sidepanel, elements.content, targetWidth);
    this.setupResizeListener();
  }

  /**
   * Applies appropriate width to the sidepanel based on settings (used after panel is already open)
   */
  private applyPanelWidth(): void {
    if (!this.panel?.opened) {
      return;
    }

    setTimeout(() => {
      const elements = this.getPanelElements();
      if (!elements.sidepanel) {
        return;
      }

      const targetWidth = this.calculateTargetWidth();
      this.applySidePanelWidth(elements.sidepanel, elements.content, targetWidth);
      this.setupResizeListener();
    }, 0);
  }

  private calculateTargetWidth(): number {
    if (this.currentSettings.resizable) {
      return this.getResizableWidth() ?? this.getSizeWidth();
    }

    return this.getSizeWidth();
  }

  private getResizableWidth(): number | null {
    const savedWidth = this.storageService.sidenavWidth;
    if (!savedWidth) {
      return null;
    }

    const width = parseInt(savedWidth, 10);
    if (isNaN(width)) {
      return null;
    }

    const minWidth = Global.minSidenavWidth;
    const maxWidth = window.innerWidth * Global.maxSidenavWidthFactor;

    return width >= minWidth && width <= maxWidth ? width : null;
  }

  private getSizeWidth(): number {
    const size = this.currentSettings.size;
    const sizeValue = this.SIZE_WIDTH_MAP[size];

    return typeof sizeValue === 'function' ? sizeValue() : sizeValue;
  }

  private applySidePanelWidth(sidenavElement: HTMLElement, contentElement: HTMLElement | null, width: number): void {
    // Clear any existing inline styles for non-resizable panels
    if (!this.currentSettings.resizable) {
      sidenavElement.style.width = '';
      sidenavElement.style.minWidth = '';
    }

    sidenavElement.style.width = `${width}px`;
    sidenavElement.style.minWidth = `${width}px`;

    this.updateContentAreaForSideMode(contentElement, width);
  }

  private updateContentAreaForSideMode(contentElement: HTMLElement | null, panelWidth: number): void {
    if (!contentElement || this.getCurrentMode() !== 'side') {
      return;
    }

    const availableWidth = window.innerWidth - panelWidth;
    contentElement.style.width = `${availableWidth}px`;
    contentElement.style.marginRight = `${panelWidth}px`;
  }

  private resetAllStyles(): void {
    try {
      const elements = this.getPanelElements();

      if (elements.sidepanel) {
        this.clearElementStyles(elements.sidepanel, ['width', 'minWidth']);
      }

      if (elements.content) {
        this.clearElementStyles(elements.content, ['width', 'marginRight']);
      }
    } catch (error) {
      this.loggingService.logException(new Error(`Failed to reset panel styles: ${this.getErrorMessage(error)}`));
    }
  }

  private clearElementStyles(element: HTMLElement, styles: string[]): void {
    for (const style of styles) {
      element.style[style as any] = '';
    }
  }

  private getPanelElements(): PanelElements {
    return {
      sidepanel: this.document.querySelector(this.SELECTORS.SIDEPANEL),
      content: this.document.querySelector(this.SELECTORS.CONTENT),
    };
  }

  private setScrollableBody(value: boolean): void {
    if (this.document?.body) {
      this.document.body.style.overflowY = value ? 'auto' : 'hidden';
    }
  }
}
